import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, MenuItem, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Chip, Tooltip, alpha, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AddCard as ReplenishIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import apiClient from '../../services/api';
import { useConfirm } from '../../hooks/useConfirm';
import { CustodyReplenishmentDto, CreateCustodyReplenishmentDto } from '../../types/custodyReplenishment.types';

const translations = {
  en: {
    title: 'Custody Replenishment',
    subtitle: 'Add funds to custody',
    amount: 'Amount',
    date: 'Date',
    paymentMethod: 'Payment Method',
    referenceNumber: 'Reference / Check #',
    notes: 'Notes',
    save: 'Save Replenishment',
    close: 'Close',
    cash: 'Cash',
    check: 'Check',
    bankTransfer: 'Bank Transfer',
    history: 'Replenishment History',
    noHistory: 'No previous replenishments',
    deleteConfirm: 'Delete this replenishment?',
    deleteSuccess: 'Replenishment deleted',
    saveFailed: 'Failed to save',
    deleteFailed: 'Failed to delete',
    amountRequired: 'Amount is required',
    custodyNumber: 'Custody #',
    employee: 'Employee',
    currentBalance: 'Current Balance',
    kwd: 'KWD',
    total: 'Total Replenishments',
    newReplenishment: 'New Replenishment',
  },
  ar: {
    title: 'تعزيز العهدة',
    subtitle: 'إضافة مبلغ للعهدة',
    amount: 'المبلغ',
    date: 'التاريخ',
    paymentMethod: 'طريقة الدفع',
    referenceNumber: 'رقم المرجع / الشيك',
    notes: 'ملاحظات',
    save: 'حفظ التعزيز',
    close: 'إغلاق',
    cash: 'نقداً',
    check: 'شيك',
    bankTransfer: 'تحويل بنكي',
    history: 'سجل التعزيزات',
    noHistory: 'لا توجد تعزيزات سابقة',
    deleteConfirm: 'هل تريد حذف هذا التعزيز؟',
    deleteSuccess: 'تم حذف التعزيز',
    saveFailed: 'فشل في الحفظ',
    deleteFailed: 'فشل في الحذف',
    amountRequired: 'المبلغ مطلوب',
    custodyNumber: 'رقم العهدة',
    employee: 'الموظف',
    currentBalance: 'الرصيد الحالي',
    kwd: 'د.ك',
    total: 'إجمالي التعزيزات',
    newReplenishment: 'تعزيز جديد',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  custody: any | null;
  onSave: () => void;
}

const CustodyReplenishmentDialog: React.FC<Props> = ({ open, onClose, custody, onSave }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = translations[isRTL ? 'ar' : 'en'];
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();
  const confirm = useConfirm();

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [amount, setAmount] = useState('');
  const [repDate, setRepDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<CustodyReplenishmentDto[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (open && custody) {
      setAmount('');
      setRepDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('');
      setReferenceNumber('');
      setNotes('');
      loadHistory();
    }
  }, [open, custody]);

  const loadHistory = async () => {
    if (!custody) return;
    setLoadingHistory(true);
    try {
      const response = await apiClient.get(`/Custodies/${custody.custodyId}/replenishments`);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load replenishments:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSave = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.warning(t.amountRequired, { position: 'top-center' });
      return;
    }
    setSaving(true);
    try {
      const dto: CreateCustodyReplenishmentDto = {
        custodyId: custody.custodyId,
        amount: amt,
        replenishmentDate: repDate,
        paymentMethod: paymentMethod || undefined,
        referenceNumber: referenceNumber || undefined,
        notes: notes || undefined,
      };
      await apiClient.post('/Custodies/replenishments', dto);
      setAmount('');
      setReferenceNumber('');
      setNotes('');
      loadHistory();
      onSave();
    } catch (error: any) {
      const msg = error.response?.data?.message || t.saveFailed;
      toast.error(msg, { position: 'top-center' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t.deleteConfirm, severity: 'error' }))) return;
    try {
      await apiClient.delete(`/Custodies/replenishments/${id}`);
      toast.success(t.deleteSuccess, { position: 'top-center' });
      loadHistory();
      onSave();
    } catch (error) {
      toast.error(t.deleteFailed, { position: 'top-center' });
    }
  };

  const totalReplenishments = history.reduce((sum, r) => sum + r.amount, 0);

  if (!custody) return null;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      transition: 'all 0.3s ease',
      '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}` },
      '&.Mui-focused': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}` },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      {/* === Header === */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: isDarkMode
            ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex' }}>
            <ReplenishIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">{t.title}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.subtitle}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* === Custody Info Bar === */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">{t.custodyNumber}</Typography>
            <Typography fontWeight="bold" color="primary">{custody.custodyNumber}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">{t.employee}</Typography>
            <Typography fontWeight="bold">{isRTL ? (custody.employeeNameAr || custody.employeeName) : custody.employeeName}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">{t.currentBalance}</Typography>
            <Typography fontWeight="bold" color={custody.currentBalance < 0 ? 'error.main' : 'success.main'}>
              {custody.currentBalance?.toFixed(3)} {t.kwd}
            </Typography>
          </Box>
          {totalReplenishments > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">{t.total}</Typography>
                <Typography fontWeight="bold" color="primary">{totalReplenishments.toFixed(3)} {t.kwd}</Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* === New Replenishment Form === */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: theme.palette.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WalletIcon fontSize="small" />
          {t.newReplenishment}
        </Typography>
        <Box ref={gridRef} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField label={t.amount} type="number" size="small" value={amount} onChange={e => setAmount(e.target.value)} sx={inputSx} inputProps={{ min: 0, step: 0.001 }} required />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.date}
              value={repDate ? new Date(repDate) : null}
              onChange={(date) => setRepDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: inputSx } }}
            />
          </LocalizationProvider>
          <TextField select label={t.paymentMethod} size="small" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} sx={inputSx}>
            <MenuItem value="">{isRTL ? '-- اختر --' : '-- Select --'}</MenuItem>
            <MenuItem value="Cash">{t.cash}</MenuItem>
            <MenuItem value="Check">{t.check}</MenuItem>
            <MenuItem value="BankTransfer">{t.bankTransfer}</MenuItem>
          </TextField>
          <TextField label={t.referenceNumber} size="small" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} sx={inputSx} />
          <TextField label={t.notes} size="small" value={notes} onChange={e => setNotes(e.target.value)} sx={{ ...inputSx, gridColumn: 'span 2' }} />
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !amount}
          sx={{
            borderRadius: 2,
            fontWeight: 'bold',
            mb: 3,
            px: 4,
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {t.save}
        </Button>

        {/* === History === */}
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReplenishIcon fontSize="small" />
          {t.history}
        </Typography>
        {loadingHistory ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={30} sx={{ color: theme.palette.primary.main }} /></Box>
        ) : history.length === 0 ? (
          <Paper elevation={0} sx={{ textAlign: 'center', py: 4, borderRadius: 2, bgcolor: isDarkMode ? alpha(theme.palette.action.hover, 0.1) : alpha('#000', 0.02) }}>
            <WalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1, opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary">{t.noHistory}</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    background: isDarkMode
                      ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 40, color: 'white' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white' }}>{t.date}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white' }}>{t.amount}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white' }}>{t.paymentMethod}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white' }}>{t.referenceNumber}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white' }}>{t.notes}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 50, color: 'white' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((r, idx) => (
                  <TableRow
                    key={r.replenishmentId}
                    hover
                    sx={{
                      '&:hover': { bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.04) },
                      bgcolor: idx % 2 === 0
                        ? (isDarkMode ? 'transparent' : alpha('#000', 0.015))
                        : 'transparent',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell sx={{ textAlign: 'center' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{r.replenishmentDate?.split('T')[0]}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: theme.palette.primary.main }}>{r.amount.toFixed(3)}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {r.paymentMethod ? (
                        <Chip
                          label={r.paymentMethod === 'Cash' ? t.cash : r.paymentMethod === 'Check' ? t.check : r.paymentMethod === 'BankTransfer' ? t.bankTransfer : r.paymentMethod}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1.5, fontWeight: 500 }}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{r.referenceNumber || '-'}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontSize: 12 }}>{r.notes || '-'}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title={isRTL ? 'حذف' : 'Delete'} arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(r.replenishmentId)}
                          sx={{
                            color: 'error.main',
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2), transform: 'scale(1.1)' },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow sx={{ bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.08) }}>
                  <TableCell colSpan={2} sx={{ textAlign: isRTL ? 'left' : 'right', fontWeight: 'bold' }}>{t.total}</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: theme.palette.primary.main, fontSize: 14 }}>{totalReplenishments.toFixed(3)}</TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, fontWeight: 'bold' }}>{t.close}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustodyReplenishmentDialog;